import type { ReactNode } from 'react'
import { ChevronLeft, Check } from 'lucide-react'

import { FILTER_OPTION_ROW_CLASS } from '@/components/filters/filter-styles'
import { cn } from '@/lib/utils'

export type FilterPickerPanelProps = {
  title: string
  onBack: () => void
  children: ReactNode
  /** Operator footer (e.g. `FilterOperatorList`). */
  footer?: ReactNode
  className?: string
}

/** Back header + body slot + optional operator footer for filter value pickers. */
export function FilterPickerPanel({
  title,
  onBack,
  children,
  footer,
  className,
}: FilterPickerPanelProps) {
  return (
    <div className={cn('flex flex-col', className)}>
      <div className="flex items-center gap-1 border-b border-border/60 px-1 py-1">
        <button
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={onBack}
          className="flex h-6 w-6 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          aria-label="Back to all filters"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
        </button>
        <span className="text-sm font-medium">{title}</span>
      </div>
      {children}
      {footer}
    </div>
  )
}

export type FilterOperatorListProps = {
  /** Accessible name for the radiogroup. */
  ariaLabel: string
  options: readonly { value: string; label: string }[]
  value: string
  onValueChange: (value: string) => void
  /** When false, omits the top border (embedded in custom popovers). Default true. */
  bordered?: boolean
  className?: string
}

/** Exclusive operator choice — trailing check, same grammar as single-select value rows. */
export function FilterOperatorList({
  ariaLabel,
  options,
  value,
  onValueChange,
  bordered = true,
  className,
}: FilterOperatorListProps) {
  return (
    <div
      className={cn(bordered && 'border-t border-border/60', 'p-1', className)}
      role="radiogroup"
      aria-label={ariaLabel}
    >
      {options.map((option) => {
        const selected = value === option.value
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onPointerDown={(event) => {
              event.preventDefault()
              if (!selected) onValueChange(option.value)
            }}
            className={cn(
              FILTER_OPTION_ROW_CLASS,
              selected && 'bg-accent text-accent-foreground',
            )}
          >
            <span className="min-w-0 flex-1 truncate">{option.label}</span>
            {selected ? <Check className="ml-auto h-4 w-4 shrink-0" aria-hidden /> : null}
          </button>
        )
      })}
    </div>
  )
}
